// @flow

import * as React from 'react';
import ReactDOM from 'react-dom';
import { get, partial, set } from 'lodash';

import type { DOMNode } from './types';

type Attributes = {
  [attribute: string]: *,
};

/*
  This is a safe wrapper around the native node.removeAttribute
  method. Makes possible passing an array of attributes to remove.
*/
export const removeNodeAttributes = (
  node?: DOMNode,
  attributes: Array<string>
) => {
  attributes.forEach(attribute => {
    if (node && typeof node.removeAttribute === 'function') {
      node.removeAttribute(attribute);
    }
  });
};

/*
  This is a safe setter/wrapper around the native node.setAttribute
  method. Makes possible passing an object of keys (attributes) and
  their values to make setting multiple attributes easier.
*/
export const setNodeAttributes = (node?: DOMNode, attributes: Attributes) => {
  Object.keys(attributes).forEach(attribute => {
    if (node && typeof node.setAttribute === 'function') {
      node.setAttribute(attribute, attributes[attribute]);
    }
  });
};

/*
  This is a safe-getter/wrapper around the native `node.getAttribute` function.
  Returns the attributes in a object.
*/
export const getNodeAttributes = (
  node?: DOMNode,
  attributes: Array<string>
) => {
  return attributes.reduce((resultsObject, attribute) => {
    if (node && typeof node.getAttribute === 'function') {
      resultsObject[attribute] = node.getAttribute(attribute);
    }
    return resultsObject;
  }, {});
};

/*
  A util for taking in an array of selectors such as ['div.some-class', 'div#some-id'],
  selecting those elements via `querySelectorAll`
  and returning those nodes in an array.
*/
export const selectAllElementsWithSelectors = (selectors: Array<string>) => {
  return selectors.reduce((nodes, selector) => {
    return [...nodes, ...document.querySelectorAll(selector)];
  }, []);
};

/*
  This function undoes the work that makNodeUnreachable does
  to make the node reachable again via tab and screen readers.
  It will remove the tabindex="-1" and aria-hidden="true" attributes,
  take the previous tabindex and aria-hidden attributes
  stored in the data-previous- prefixed attributes,
  apply those previous values and finally remove the data-previous attributes.
*/
export const makeNodeReachable = (node: DOMNode) => {
  const previousAttributes = [
    'data-previous-tabindex',
    'data-previous-aria-hidden',
  ];
  const attributes = getNodeAttributes(node, previousAttributes);
  const previousTabIndex = attributes['data-previous-tabindex'];
  const previousAriaHidden = attributes['data-previous-aria-hidden'];
  removeNodeAttributes(node, ['tabindex', 'aria-hidden']);
  if (previousTabIndex) {
    setNodeAttributes(node, { tabindex: previousTabIndex });
  }
  if (previousAriaHidden) {
    setNodeAttributes(node, { 'aria-hidden': previousAriaHidden });
  }
  removeNodeAttributes(node, previousAttributes);
};

/*
  This util stores the existing tabindex and aria-hidden attributes
  in data-previous- prefixed values and then sets the tabindex to -1
  and the aria-hidden to true. This should keep the element out of
  the tab order and hide it from screen readers, and will allow us
  to reset the tabindex to aria-hidden to the previous value when done.
*/
export const makeNodeUnreachable = (node: DOMNode) => {
  const attributes = getNodeAttributes(node, ['tabindex', 'aria-hidden']);
  const existingTabIndex = attributes.tabindex;
  const existingAriaHidden = attributes['aria-hidden'];
  if (existingTabIndex) {
    setNodeAttributes(node, {
      'data-previous-tabindex': existingTabIndex,
    });
  }
  if (existingAriaHidden) {
    setNodeAttributes(node, {
      'data-previous-aria-hidden': existingAriaHidden,
    });
  }
  setNodeAttributes(node, { tabindex: '-1', 'aria-hidden': true });
};

/*
  This is a util to make performing an operation on a node list easier.
  It recursively performs an operation on its node, and if the node has children,
  it will drill down and perform the operation on the children as well,
  until there are no more children.
*/
export const performOperationOnNodeList = (
  operation: (node: DOMNode) => *,
  nodeList: *
) => {
  /*
    Spreading the nodeList is necessary because when selecting
    items from the DOM via querySelectorAll, a nodeList is returned,
    which is like an array but is not iterable.
    This transforms the list into an iterable array.
  */
  const nodeArray = [...nodeList];
  nodeArray.forEach(node => {
    operation(node);
    if (node && node.childNodes) {
      const childArray = [...node.childNodes];
      performOperationOnNodeList(operation, childArray);
    }
  });
};

export const makeNodesReachable = partial(
  performOperationOnNodeList,
  makeNodeReachable
);

export const makeNodesUnreachable = partial(
  performOperationOnNodeList,
  makeNodeUnreachable
);

export const getRef = (Component: any, key: string): any => {
  // Regardless of whether the ref is a real DOM node or a component,
  // always return the representative DOM node.
  const ref = get(Component, ['dangerousRefs', key]);
  return ReactDOM.findDOMNode(ref);
};

export const setRef = (Component: any, key: string) => (
  nodeOrComponent: React.Node | Element
) => {
  // The ref might be a real DOM node _or_ a representation of a component
  set(Component, ['dangerousRefs', key], nodeOrComponent);
};
